import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

type Theme = "light" | "dark";
/** Which dark the free/guest pages use: near-black (AMOLED) or dim grey. */
export type DarkStyle = "black" | "gray";

const DARK_STYLE_KEY = "dealhawk_dark_style";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
  darkStyle: DarkStyle;
  setDarkStyle: (style: DarkStyle) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("theme") as Theme;
      if (stored) return stored;
    }
    // Default to light mode
    return "light";
  });

  const [darkStyle, setDarkStyleState] = useState<DarkStyle>(() => {
    if (typeof window !== "undefined") {
      try {
        if (localStorage.getItem(DARK_STYLE_KEY) === "gray") return "gray";
      } catch {
        // Storage blocked: near-black.
      }
    }
    return "black";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    // `.dark.dim` in index.css swaps the free/guest greys; it only applies inside dark.
    root.classList.toggle("dim", darkStyle === "gray");
    localStorage.setItem("theme", theme);
    try {
      localStorage.setItem(DARK_STYLE_KEY, darkStyle);
    } catch {
      // Storage blocked: the choice lasts for this visit.
    }
  }, [theme, darkStyle]);

  const toggleTheme = () => setThemeState((prev) => (prev === "light" ? "dark" : "light"));
  const setTheme = (newTheme: Theme) => setThemeState(newTheme);
  const setDarkStyle = (style: DarkStyle) => setDarkStyleState(style);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, isDark: theme === "dark", darkStyle, setDarkStyle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
